import { useMutation } from "@apollo/client";
import { datadogRum } from "@datadog/browser-rum";
import { writeStorage } from "@rehooks/local-storage";
import cx from "classnames";
import React, { useState } from "react";

import { FiltersContext } from "components/Filters";
import { getDefaultViewStorageKey } from "components/Filters/helpers";
import FlashContext from "components/FlashMessages/FlashContext";
import PageLoading from "components/loading/PageLoading";
import Button from "ds/components/Button";
import FormField from "ds/components/Form/Field";
import Input from "ds/components/Input";
import Toggle from "ds/components/Toggle";
import useTypedContext from "hooks/useTypedContext";

import { SavedFiltersContext } from "../Context";
import { SAVE_VIEW } from "../gql";
import { getFiltersFromSavedData, getSavedViewData } from "../helpers";
import { useSortOptions } from "../useSortOptions";
import styles from "./styles.module.css";

type FiltersSavedViewsCreateFormProps = {
  closeForm: () => void;
};

const FiltersSavedViewsCreateForm = ({ closeForm }: FiltersSavedViewsCreateFormProps) => {
  const { reportSuccess, onError } = useTypedContext(FlashContext);
  const { activeFilters, filterType, setCurrentView, defaultEmptyFilterView } =
    useTypedContext(FiltersContext);

  const { loading, privateViews } = useTypedContext(SavedFiltersContext);

  const [name, setName] = useState("");
  const [defaultChecked, setDefaultChecked] = useState(false);
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  };

  const [saveView] = useMutation(SAVE_VIEW, { refetchQueries: ["SavedViewsList"] });
  const sortOptions = useSortOptions();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const { data, error } = getSavedViewData(activeFilters, sortOptions);

    if (error) {
      return onError(error);
    }

    saveView({
      variables: {
        input: {
          name,
          isPublic: false,
          type: filterType,
          data,
        },
      },
    })
      .then(({ data: { savedFilterCreate } }) => {
        if (savedFilterCreate) {
          reportSuccess({ message: `View ${savedFilterCreate.name} was successfully created` });
          const { order, filters, sortOption, sortDirection, search } = getFiltersFromSavedData(
            savedFilterCreate.data,
            defaultEmptyFilterView
          );
          if (defaultChecked) {
            writeStorage(getDefaultViewStorageKey(filterType), savedFilterCreate.id);
          }
          setCurrentView({
            name: savedFilterCreate.name,
            id: savedFilterCreate.id,
            isPublic: savedFilterCreate.isPublic,
            order,
            filters,
            isMine: true,
            sortOption,
            sortDirection,
            search,
          });

          setName("");
          setDefaultChecked(false);
          datadogRum.addAction("[Saved Views] Save View");
        }
      })
      .catch(onError);

    closeForm();
  };

  const error = privateViews?.find((view) => view.name === name)
    ? "View name already exists"
    : undefined;

  if (loading) {
    return <PageLoading />;
  }

  return (
    <form onSubmit={handleSubmit} className={cx(styles.wrapper, styles.wrapperSticky)}>
      <FormField
        error={error}
        label="Name"
        helperText="Save your filters, sorting and search as a view."
      >
        {({ ariaInputProps }) => (
          <Input
            onChange={handleNameChange}
            value={name}
            placeholder="Name your view"
            error={!!error}
            autoFocus
            {...ariaInputProps}
          />
        )}
      </FormField>
      <FormField>
        <Toggle
          variant="checkbox"
          isSelected={defaultChecked}
          id="default"
          onChange={setDefaultChecked}
          aria-label={defaultChecked ? "Unset as your default" : "Set as your default"}
        >
          Use as your default
        </Toggle>
      </FormField>
      <div className={styles.buttons}>
        <Button variant="secondary" onPress={closeForm}>
          Cancel
        </Button>
        <Button variant="primary" type="submit" disabled={!name || !!error}>
          Save
        </Button>
      </div>
    </form>
  );
};

export default FiltersSavedViewsCreateForm;
